import { Component, computed, OnDestroy, OnInit, Signal, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Method } from '@bo/ng-api';
import { RegisterService } from '../../../../_services/register-service/register.service';
import { BoFormsModule } from '@bo/ng-forms';
import { PasswordFieldComponent } from '../../../../shared/components/forms/password-field/password-field.component';
import { SubmitComponent } from '../../../../shared/components/forms/submit/submit.component';
import { ValidTokenData, VerifyAccountData, VerifyAccountResponse } from '../../../../_interfaces/verify-account';
import { RouterSwitchBtnComponent } from '../../../../shared/components/router-switch-btn/router-switch-btn.component';
import { SetAppStateFromSignUpPasswordSet } from '../../../../store/app/app.actions';
import { EmployeeApiService } from '../../../../_services/employee-service/api/employee-api.service';
import { EmployeeRoleType } from '../../../../_interfaces/employee.interface';
import {
  BaseCreatePasswordComponent,
  ErrorDetail,
  Params,
} from '../../../../shared/components/create-password/create-password.component';
import { ProgressBarService } from '../../../../_services/progress-bar-service/progress-bar.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';

@Component({
  selector: 'app-register-password-employee',
  imports: [BoFormsModule, PasswordFieldComponent, SubmitComponent, RouterSwitchBtnComponent, LoaderComponent],
  templateUrl: '../../../../shared/components/create-password/create-password.component.html',
})
export class CreatePasswordEmployeeComponent
  extends BaseCreatePasswordComponent<VerifyAccountResponse>
  implements OnInit, OnDestroy
{
  override readonly store: Store;
  readonly router = inject(Router);
  readonly employeeApiService = inject(EmployeeApiService);
  readonly registerService = inject(RegisterService);
  readonly route = inject(ActivatedRoute);
  readonly progressBarService = inject(ProgressBarService);

  override resend$: Observable<VerifyAccountResponse> | undefined;
  override formLabel: string = 'employeeSignup';
  userType!: EmployeeRoleType;

  override invalidDetail: ErrorDetail = {
    title: 'The link has expired.',
    text: ['Please reach out to your admin for more information.'],
  };
  override notFoundDetail: ErrorDetail = {
    title: "We couldn't find your account",
    text: [
      'If you have already created a password, use your company email and new password to sign in to your Paystashio app.',
    ],
  };

  readonly params: Signal<Params> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return {
      code: queryParams['code'] as string,
      id: queryParams['employee_signup_id'] as string,
    };
  });

  constructor() {
    const store = inject(Store);
    super();
    this.store = store;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.userType = this.route.snapshot.queryParams['type'];
    const data: ValidTokenData = { code: this.params().code };
    this.employeeApiService
      .verifyEmployeeAccountTokenValid(this.params().id, data)
      .subscribe(this.validateResponseHandler());
    this.progressBarService.setHideProgress(true); // Set to true to hide the progress bar
  }

  override createForm(): void {
    const URL_EMPLOYEE_SIGNUP = this.employeeApiService.URL_EMPLOYEE_SIGNUP;
    const URL_VERIFY = this.employeeApiService.URL_VERIFY;
    this.buildFromOptions(
      this.registerService.getOptions(`${URL_EMPLOYEE_SIGNUP}/${this.params().id}/${URL_VERIFY}/`, Method.POST),
    ).subscribe();
  }

  override handleSubmitSuccess(res: VerifyAccountResponse): void {
    if (this.userType === EmployeeRoleType.USER) {
      this.router.navigate(['sign-up', 'employee', 'success']);
    } else {
      this.store.dispatch(new SetAppStateFromSignUpPasswordSet(res)).subscribe({
        next: () => {
          this.router.navigate(['sign-up', 'employee', 'verify-phone']);
        },
      });
    }
  }

  override handleSubmitError(res: HttpErrorResponse): void {
    if (res.error.card) {
      this.showFieldError.set(res.error.card);
    } else {
      super.handleSubmitError(res);
    }
  }

  override submitData(formData: VerifyAccountData): Observable<VerifyAccountResponse> {
    formData.code = this.params().code;
    return this.employeeApiService.verifyEmployeeAccount(this.params().id, formData);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.progressBarService.setHideProgress(false); // Set to false to hide the progress bar
  }
}
